/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from './supabase';
import {
  StatusPill, SegmentedPills, PrimaryBtn, SecondaryBtn,
  Toast, useToastAutoDismiss,
} from './components/systemKit';
import RecipeDrawer from './components/RecipeDrawer';
import {
  confirmLink, relinkToFoodItem, setManualCost, markUnlinked,
  bulkConfirm, refreshAllMenuItemHealth, fuzzyMatchFoodItems,
} from './lib/recipeHealth';

// Fetch the signed-in user's email for audit-attribution helper calls.
// Mirrors the pattern used in RecipeDrawer + parseGuardrails consumers.
function useCurrentUserEmail() {
  const [email, setEmail] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase.auth.getUser();
      if (!cancelled) setEmail(data?.user?.email || null);
    })();
    return () => { cancelled = true; };
  }, []);
  return email;
}

// ─── Recipe Health page ─────────────────────────────────────────────────────
// Queue surface. Default-filtered to "Pending review" — the actionable view.
// Operators work top-to-bottom through their menu_items, confirming or
// fixing the AI matches that landed on first menu upload, and from then on
// the queue stays clean.
//
// Lives at currentPage='menu', menuTab='health'. Sidebar entry under MENU
// nav group.
//
// Architectural parallel: this is to recipe_ingredients what
// <DataReview> is to needs_review-flagged food_items. Same shape:
// grouped queue, per-row actions, audit log writes, idempotent bulk ops.
//
// See DESIGN_SYSTEM.md §Recipe Health surface.

function fmt$(n) { return `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`; }
function fmt$Short(n) {
  if (n == null || !isFinite(n)) return '\u2014';
  if (Math.abs(n) >= 1000) return `$${(n / 1000).toFixed(1).replace(/\.0$/, '')}k`;
  return `$${Math.round(n).toLocaleString('en-US')}`;
}

const FILTERS = [
  { key: 'pending',  label: 'Pending review' },
  { key: 'unlinked', label: 'Unlinked' },
  { key: 'manual',   label: 'Manual cost' },
  { key: 'linked',   label: 'Linked' },
  { key: 'flag',     label: 'Has data flag' },
  { key: 'all',      label: 'All' },
];

export default function RecipeHealth({ companyId }) {
  const userEmail = useCurrentUserEmail();
  const [toast, setToast] = useState(null);
  useToastAutoDismiss(toast, setToast);
  const [loading, setLoading] = useState(true);
  const [menuItems, setMenuItems] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [foodItems, setFoodItems] = useState([]);
  const [filter, setFilter] = useState('pending'); // default per spec
  const [search, setSearch] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [collapsedItems, setCollapsedItems] = useState(new Set());
  const [drawerItem, setDrawerItem] = useState(null);
  const [drawerScrollFirstPending, setDrawerScrollFirstPending] = useState(false);
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [bulkRunning, setBulkRunning] = useState(false);
  // Per-row inline link search + manual cost UI state on the queue page
  // (mirrors the drawer's per-row state machine without re-rendering the
  // full drawer). Keys = recipe_ingredient.id.
  const [linkSearchOpen, setLinkSearchOpen] = useState({});
  const [linkSearchQuery, setLinkSearchQuery] = useState({});
  const [manualCostOpen, setManualCostOpen] = useState({});
  const [manualCostInput, setManualCostInput] = useState({});

  useEffect(() => {
    if (!companyId) return;
    fetchAll();
  }, [companyId]);

  // Refresh pricing_health for every menu_item on first load. Defensive —
  // covers any direct DB tampering since the last visit.
  useEffect(() => {
    if (!companyId) return;
    refreshAllMenuItemHealth(companyId).catch(() => {});
  }, [companyId]);

  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(search.trim().toLowerCase()), 200);
    return () => clearTimeout(t);
  }, [search]);

  async function fetchAll() {
    setLoading(true);
    const [miRes, riRes, fiRes] = await Promise.all([
      supabase.from('menu_items')
        .select('id, name, category, menu_price, plate_cost, margin_pct, pricing_health, is_active')
        .eq('company_id', companyId)
        .eq('is_active', true)
        .order('category')
        .order('name')
        .range(0, 9999),
      supabase.from('recipe_ingredients')
        .select('*')
        .eq('company_id', companyId)
        .range(0, 49999),
      supabase.from('food_items')
        .select('id, normalized_name, category, unit, current_price, price_per_lb, is_catch_weight, needs_review')
        .eq('company_id', companyId)
        .range(0, 9999),
    ]);
    setMenuItems(miRes.data || []);
    setIngredients(riRes.data || []);
    setFoodItems(fiRes.data || []);
    setLoading(false);
  }

  // Compose menu_items with their ingredients attached for the drawer.
  const itemsWithIngredients = useMemo(() => {
    return menuItems.map(mi => ({
      ...mi,
      ingredients: ingredients.filter(ri => ri.menu_item_id === mi.id),
    }));
  }, [menuItems, ingredients]);

  // ── Filter + group ingredients for the queue ──────────────────────────
  const filteredRows = useMemo(() => {
    const foodItemsById = {};
    foodItems.forEach(fi => { foodItemsById[fi.id] = fi; });

    const passesFilter = (ri) => {
      if (filter === 'all') return true;
      if (filter === 'pending') return ri.link_status === 'pending_review';
      if (filter === 'unlinked') return ri.link_status === 'unlinked_no_cost';
      if (filter === 'manual') return ri.link_status === 'unlinked_manual_cost';
      if (filter === 'linked') return ri.link_status === 'linked';
      if (filter === 'flag') {
        const fi = ri.food_item_id ? foodItemsById[ri.food_item_id] : null;
        return !!fi?.needs_review;
      }
      return true;
    };

    const passesSearch = (ri, mi) => {
      if (!searchDebounced) return true;
      return (ri.ingredient_name || '').toLowerCase().includes(searchDebounced)
          || (mi.name || '').toLowerCase().includes(searchDebounced);
    };

    // Group by menu_item; each group renders only when at least one ingredient
    // passes the filter (or all of its ingredients pass the search).
    const grouped = [];
    for (const mi of itemsWithIngredients) {
      const visible = (mi.ingredients || [])
        .filter(ri => passesFilter(ri) && passesSearch(ri, mi));
      if (visible.length === 0) continue;
      // Sort within a menu_item: pending → unlinked → manual → linked
      const stateOrder = { pending_review: 0, unlinked_no_cost: 1, unlinked_manual_cost: 2, linked: 3 };
      visible.sort((a, b) => (stateOrder[a.link_status] ?? 9) - (stateOrder[b.link_status] ?? 9));
      grouped.push({ ...mi, visibleIngredients: visible, allIngredients: mi.ingredients });
    }
    // Sort menu_items: those with pending_review ingredients first
    // (most actionable). Then alphabetical within tier.
    grouped.sort((a, b) => {
      const aPending = a.visibleIngredients.some(ri => ri.link_status === 'pending_review') ? 0 : 1;
      const bPending = b.visibleIngredients.some(ri => ri.link_status === 'pending_review') ? 0 : 1;
      if (aPending !== bPending) return aPending - bPending;
      return (a.name || '').localeCompare(b.name || '');
    });
    return grouped;
  }, [itemsWithIngredients, filter, searchDebounced, foodItems]);

  // ── Summary counts ─────────────────────────────────────────────────────
  const summary = useMemo(() => {
    const c = { total: ingredients.length, pending: 0, unlinked: 0, manual: 0, linked: 0, flag: 0 };
    const foodItemsById = {};
    foodItems.forEach(fi => { foodItemsById[fi.id] = fi; });
    for (const ri of ingredients) {
      if (ri.link_status === 'pending_review') c.pending++;
      else if (ri.link_status === 'unlinked_no_cost') c.unlinked++;
      else if (ri.link_status === 'unlinked_manual_cost') c.manual++;
      else if (ri.link_status === 'linked') c.linked++;
      const fi = ri.food_item_id ? foodItemsById[ri.food_item_id] : null;
      if (fi?.needs_review) c.flag++;
    }
    return c;
  }, [ingredients, foodItems]);

  const allHealthy = summary.total > 0 && summary.pending === 0 && summary.unlinked === 0;

  // ── Selection model for bulk-confirm ──────────────────────────────────
  function toggleSelected(id) {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }
  function clearSelected() { setSelectedIds(new Set()); }
  // "Select all visible pending_review" — convenience for the bulk action
  function selectAllVisiblePending() {
    const ids = new Set();
    for (const g of filteredRows) {
      for (const ri of g.visibleIngredients) {
        if (ri.link_status === 'pending_review') ids.add(ri.id);
      }
    }
    setSelectedIds(ids);
  }

  // Wrap every mutation in a try/catch that surfaces failures as a
  // critical-tone toast. Success toasts fire before fetchAll so the
  // operator sees the receipt instantly (the refetch can take a beat
  // on slower connections).
  function requireSignedIn() {
    if (!userEmail) {
      setToast({ kind: 'warning', text: 'Sign in to update linkages.' });
      return false;
    }
    return true;
  }
  async function safeRun(promise, successToast) {
    try {
      await promise;
      if (successToast) setToast(successToast);
      await fetchAll();
    } catch (err) {
      setToast({ kind: 'critical', text: `Action failed: ${err.message || 'unknown error'}` });
    }
  }

  async function handleBulkConfirm() {
    if (!requireSignedIn()) return;
    setBulkRunning(true);
    const count = selectedIds.size;
    try {
      await bulkConfirm({ companyId, recipeIngredientIds: Array.from(selectedIds), userEmail });
      setToast({ kind: 'success', text: `Confirmed ${count} ingredient${count === 1 ? '' : 's'}` });
      setSelectedIds(new Set());
      setBulkConfirmOpen(false);
      await fetchAll();
    } catch (err) {
      setToast({ kind: 'critical', text: `Bulk confirm failed: ${err.message || 'unknown error'}` });
    } finally {
      setBulkRunning(false);
    }
  }

  // ── Per-row actions ────────────────────────────────────────────────────
  async function handleConfirm(ri) {
    if (!requireSignedIn()) return;
    const linkedFi = ri.food_item_id ? foodItems.find(f => f.id === ri.food_item_id) : null;
    const successToast = {
      kind: 'success',
      text: linkedFi ? `Linked to ${linkedFi.normalized_name}` : 'Link confirmed',
    };
    await safeRun(confirmLink({ companyId, recipeIngredientId: ri.id, userEmail }), successToast);
  }
  async function handleMarkUnlinked(ri) {
    if (!requireSignedIn()) return;
    await safeRun(
      markUnlinked({ companyId, recipeIngredientId: ri.id, userEmail }),
      { kind: 'success', text: 'Marked unpriced \u2014 1 ingredient excluded from plate cost' }
    );
  }
  async function handleRelink(ri, foodItem, score) {
    if (!requireSignedIn()) return;
    // Compute new unit_cost matching MenuProfitability's rule.
    let foodUnit = foodItem.unit || 'each';
    let costPerUnit = Number(foodItem.current_price) || 0;
    if (foodItem.price_per_lb != null && (foodUnit.toLowerCase() === 'case' || foodUnit.toLowerCase() === 'cs')) {
      costPerUnit = Number(foodItem.price_per_lb);
      foodUnit = 'lb';
    }
    const conv = convertQty(1, ri.unit, foodUnit);
    const newUnitCost = conv != null ? costPerUnit * conv : (Number(ri.unit_cost) || 0);
    await safeRun(
      relinkToFoodItem({
        companyId, recipeIngredientId: ri.id, foodItemId: foodItem.id, newUnitCost,
        matchConfidence: score || null, userEmail,
      }),
      { kind: 'success', text: `Re-linked to ${foodItem.normalized_name}` }
    );
    setLinkSearchOpen(prev => ({ ...prev, [ri.id]: false }));
    setLinkSearchQuery(prev => ({ ...prev, [ri.id]: '' }));
  }
  async function handleSaveManualCost(ri) {
    if (!requireSignedIn()) return;
    const value = parseFloat(manualCostInput[ri.id]);
    if (!isFinite(value) || value < 0) return;
    await safeRun(
      setManualCost({ companyId, recipeIngredientId: ri.id, manualUnitCost: value, userEmail }),
      { kind: 'success', text: `Manual cost set: $${value.toFixed(2)}/${ri.unit}` }
    );
    setManualCostOpen(prev => ({ ...prev, [ri.id]: false }));
    setManualCostInput(prev => ({ ...prev, [ri.id]: '' }));
  }

  function toggleCollapsed(menuItemId) {
    setCollapsedItems(prev => {
      const next = new Set(prev);
      if (next.has(menuItemId)) next.delete(menuItemId); else next.add(menuItemId);
      return next;
    });
  }

  return (
    <div className="flex-1 overflow-y-auto p-6 bg-gray-50">
      <div className="max-w-5xl mx-auto">
        {/* Page header */}
        <div className="mb-6">
          <h1 className="text-2xl font-semibold" style={{ color: 'var(--text-primary)', letterSpacing: '-0.01em' }}>Recipe Health</h1>
          <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
            Review and confirm ingredient linkages so margin numbers stay accurate.
          </p>
        </div>

        {/* Summary chips — conditional rendering. When everything is healthy
            (zero pending, zero unlinked, zero flags), collapse to a single
            confirmation line. Otherwise render only the nonzero states.
            See DESIGN_SYSTEM.md §Conditional-stat rendering. */}
        {summary.total > 0 && summary.pending === 0 && summary.unlinked === 0 && summary.flag === 0 ? (
          <div className="text-sm mb-4" style={{ color: 'var(--text-success)' }}>
            {`All ${summary.total} ingredient${summary.total === 1 ? '' : 's'} linked \u2713${summary.manual > 0 ? ` \u00B7 ${summary.manual} manual cost` : ''}`}
          </div>
        ) : (
          <div className="flex items-center gap-2 flex-wrap mb-4">
            <SummaryChip label={summary.total === 1 ? 'ingredient' : 'ingredients'} count={summary.total} tone="neutral" />
            {summary.pending > 0 && <SummaryChip label="pending review" count={summary.pending} tone="amber" />}
            {summary.unlinked > 0 && <SummaryChip label="unlinked" count={summary.unlinked} tone="amber" />}
            {summary.manual > 0 && <SummaryChip label="manual cost" count={summary.manual} tone="neutral" />}
            {summary.linked > 0 && <SummaryChip label="linked" count={summary.linked} tone="green" />}
            {summary.flag > 0 && <SummaryChip label="has data flag" count={summary.flag} tone="amber" />}
          </div>
        )}

        {/* Filter pills + search */}
        <div className="flex items-center gap-3 mb-4 flex-wrap">
          <SegmentedPills
            value={filter}
            onChange={setFilter}
            options={FILTERS}
            size="sm"
          />
          <div className="relative" style={{ width: 240 }}>
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search ingredient or menu item"
              className="w-full rounded-lg px-3 py-1.5 text-sm focus:outline-none"
              style={{ border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-primary)' }}
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                aria-label="Clear search"
                style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 14, padding: 4, lineHeight: 1 }}
              >{'\u2715'}</button>
            )}
          </div>
          {selectedIds.size === 0 && filter === 'pending' && filteredRows.length > 0 && (
            <SecondaryBtn onClick={selectAllVisiblePending} className="!text-xs !px-3 !py-1.5">
              Select all pending in view
            </SecondaryBtn>
          )}
        </div>

        {/* Bulk action bar — only when items selected */}
        {selectedIds.size > 0 && (
          <div
            className="rounded-lg p-3 mb-4 flex items-center justify-between gap-3 flex-wrap"
            style={{ background: 'var(--bg-info-subtle)', border: '1px solid var(--border)' }}
          >
            <div className="text-sm" style={{ color: 'var(--text-primary)' }}>
              {`${selectedIds.size} ingredient${selectedIds.size === 1 ? '' : 's'} selected`}
            </div>
            <div className="flex items-center gap-2">
              <SecondaryBtn onClick={clearSelected} className="!text-xs !px-3 !py-1.5">Clear</SecondaryBtn>
              <PrimaryBtn onClick={() => setBulkConfirmOpen(true)} className="!text-xs !px-3 !py-1.5">
                Confirm selected as linked
              </PrimaryBtn>
            </div>
          </div>
        )}

        {/* Body */}
        {loading ? (
          <div className="rounded-lg p-12 text-center text-sm" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', color: 'var(--text-muted)' }}>
            Loading…
          </div>
        ) : allHealthy ? (
          <div className="rounded-lg p-12 text-center" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
            <div className="text-3xl mb-3" aria-hidden>{'\u2705'}</div>
            <div className="text-sm font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>All recipes are linked and confirmed.</div>
            <div className="text-xs" style={{ color: 'var(--text-muted)' }}>Nice work.</div>
          </div>
        ) : filteredRows.length === 0 ? (
          <div className="rounded-lg p-12 text-center text-sm" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', color: 'var(--text-muted)' }}>
            No ingredients match this filter.
          </div>
        ) : (
          <div className="space-y-3">
            {filteredRows.map(group => (
              <MenuItemGroup
                key={group.id}
                group={group}
                foodItems={foodItems}
                collapsed={collapsedItems.has(group.id)}
                onToggleCollapse={() => toggleCollapsed(group.id)}
                onOpenDrawer={() => { setDrawerItem(group); setDrawerScrollFirstPending(true); }}
                selectedIds={selectedIds}
                onToggleSelected={toggleSelected}
                linkSearchOpen={linkSearchOpen}
                linkSearchQuery={linkSearchQuery}
                manualCostOpen={manualCostOpen}
                manualCostInput={manualCostInput}
                onOpenLinkSearch={(id, name) => {
                  setLinkSearchOpen(prev => ({ ...prev, [id]: true }));
                  setLinkSearchQuery(prev => ({ ...prev, [id]: name || '' }));
                }}
                onCloseLinkSearch={(id) => {
                  setLinkSearchOpen(prev => ({ ...prev, [id]: false }));
                  setLinkSearchQuery(prev => ({ ...prev, [id]: '' }));
                }}
                onLinkSearchQueryChange={(id, q) => setLinkSearchQuery(prev => ({ ...prev, [id]: q }))}
                onPickFoodItem={handleRelink}
                onOpenManualCost={(id, currentCost) => {
                  setManualCostOpen(prev => ({ ...prev, [id]: true }));
                  setManualCostInput(prev => ({ ...prev, [id]: currentCost ? String(currentCost) : '' }));
                }}
                onCloseManualCost={(id) => {
                  setManualCostOpen(prev => ({ ...prev, [id]: false }));
                  setManualCostInput(prev => ({ ...prev, [id]: '' }));
                }}
                onManualCostInputChange={(id, v) => setManualCostInput(prev => ({ ...prev, [id]: v }))}
                onSaveManualCost={handleSaveManualCost}
                onConfirm={handleConfirm}
                onMarkUnlinked={handleMarkUnlinked}
              />
            ))}
          </div>
        )}
      </div>

      {/* Recipe drawer (slide-over) */}
      <RecipeDrawer
        open={drawerItem != null}
        onClose={() => { setDrawerItem(null); setDrawerScrollFirstPending(false); }}
        companyId={companyId}
        item={drawerItem}
        foodItems={foodItems}
        onSaved={fetchAll}
        scrollToFirstPending={drawerScrollFirstPending}
        onToast={setToast}
      />

      {/* Mutation feedback toast (success / failure / sign-in warnings) */}
      <Toast toast={toast} onDismiss={() => setToast(null)} />

      {/* Bulk confirm safety modal */}
      {bulkConfirmOpen && (
        <BulkConfirmModal
          count={selectedIds.size}
          running={bulkRunning}
          onCancel={() => setBulkConfirmOpen(false)}
          onConfirm={handleBulkConfirm}
        />
      )}
    </div>
  );
}

// ── Small unit-conversion helper (mirrors RecipeDrawer's convertQty) ────
function convertQty(qty, fromUnit, toUnit) {
  if (!fromUnit || !toUnit) return null;
  if (fromUnit === toUnit) return qty;
  const f = fromUnit.toLowerCase(), t = toUnit.toLowerCase();
  const pairs = {
    'oz_lb': 1 / 16, 'lb_oz': 16,
    'g_lb': 0.00220462, 'lb_g': 453.592,
    'oz_g': 28.3495, 'g_oz': 1 / 28.3495,
    'ml_oz': 1 / 29.5735, 'oz_ml': 29.5735,
    'tbsp_oz': 0.5, 'oz_tbsp': 2,
    'tsp_oz': 1 / 6, 'oz_tsp': 6,
    'cup_oz': 8, 'oz_cup': 1 / 8,
  };
  const k = `${f}_${t}`;
  return pairs[k] != null ? qty * pairs[k] : null;
}

// ── Sub-components ─────────────────────────────────────────────────────────

function SummaryChip({ label, count, tone }) {
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full"
      style={{
        background: tone === 'amber' ? 'var(--bg-warning-subtle)' : tone === 'green' ? 'var(--bg-success-subtle)' : 'var(--bg-subtle)',
        color: tone === 'amber' ? 'var(--text-warning)' : tone === 'green' ? 'var(--text-success)' : 'var(--text-secondary)',
        fontSize: 12, fontWeight: 500, padding: '4px 10px',
      }}
    >
      <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{count}</span>
      <span>{label}</span>
    </span>
  );
}

function MenuItemGroup({
  group, foodItems, collapsed, onToggleCollapse, onOpenDrawer,
  selectedIds, onToggleSelected,
  linkSearchOpen, linkSearchQuery, manualCostOpen, manualCostInput,
  onOpenLinkSearch, onCloseLinkSearch, onLinkSearchQueryChange, onPickFoodItem,
  onOpenManualCost, onCloseManualCost, onManualCostInputChange, onSaveManualCost,
  onConfirm, onMarkUnlinked,
}) {
  const pendingCount = group.visibleIngredients.filter(ri => ri.link_status === 'pending_review').length;
  const unlinkedCount = group.visibleIngredients.filter(ri => ri.link_status === 'unlinked_no_cost').length;

  return (
    <div className="rounded-lg overflow-hidden" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
      {/* Group header */}
      <div
        className="px-4 py-3 flex items-center justify-between gap-3 flex-wrap cursor-pointer"
        style={{ borderBottom: collapsed ? 'none' : '1px solid var(--border)', background: 'var(--bg-subtle)' }}
        onClick={onToggleCollapse}
      >
        <div className="flex items-center gap-2 flex-wrap min-w-0">
          <button
            onClick={(e) => { e.stopPropagation(); onOpenDrawer(); }}
            className="text-sm font-semibold truncate text-left"
            style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--text-primary)' }}
            onMouseEnter={e => { e.currentTarget.style.color = 'var(--accent-primary)'; }}
            onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-primary)'; }}
          >
            {group.name}
          </button>
          <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
            {`${group.category || 'Uncategorized'} \u00B7 ${fmt$Short(group.menu_price)}`}
          </span>
          {pendingCount > 0 && <StatusPill tone="amber">{`${pendingCount} to review`}</StatusPill>}
          {unlinkedCount > 0 && <StatusPill tone="amber">{`${unlinkedCount} unlinked`}</StatusPill>}
        </div>
        <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
          {collapsed ? `${group.visibleIngredients.length} ingredient${group.visibleIngredients.length === 1 ? '' : 's'} \u2192` : '\u2193'}
        </span>
      </div>

      {/* Group rows */}
      {!collapsed && (
        <div>
          {group.visibleIngredients.map((ri, i) => (
            <QueueRow
              key={ri.id}
              ri={ri}
              isLast={i === group.visibleIngredients.length - 1}
              foodItems={foodItems}
              isSelected={selectedIds.has(ri.id)}
              onToggleSelected={() => onToggleSelected(ri.id)}
              linkSearchOpen={!!linkSearchOpen[ri.id]}
              linkSearchQuery={linkSearchQuery[ri.id] || ''}
              manualCostOpen={!!manualCostOpen[ri.id]}
              manualCostInput={manualCostInput[ri.id] || ''}
              onOpenLinkSearch={() => onOpenLinkSearch(ri.id, ri.ingredient_name)}
              onCloseLinkSearch={() => onCloseLinkSearch(ri.id)}
              onLinkSearchQueryChange={(q) => onLinkSearchQueryChange(ri.id, q)}
              onPickFoodItem={(fi, score) => onPickFoodItem(ri, fi, score)}
              onOpenManualCost={() => onOpenManualCost(ri.id, ri.unit_cost)}
              onCloseManualCost={() => onCloseManualCost(ri.id)}
              onManualCostInputChange={(v) => onManualCostInputChange(ri.id, v)}
              onSaveManualCost={() => onSaveManualCost(ri)}
              onConfirm={() => onConfirm(ri)}
              onMarkUnlinked={() => onMarkUnlinked(ri)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function QueueRow({
  ri, isLast, foodItems, isSelected, onToggleSelected,
  linkSearchOpen, linkSearchQuery, manualCostOpen, manualCostInput,
  onOpenLinkSearch, onCloseLinkSearch, onLinkSearchQueryChange, onPickFoodItem,
  onOpenManualCost, onCloseManualCost, onManualCostInputChange, onSaveManualCost,
  onConfirm, onMarkUnlinked,
}) {
  const linkedFoodItem = ri.food_item_id ? foodItems.find(f => f.id === ri.food_item_id) : null;
  const stateChip = (() => {
    switch (ri.link_status) {
      case 'linked':                return <StatusPill tone="green">Linked</StatusPill>;
      case 'pending_review':        return <StatusPill tone="amber">Pending review</StatusPill>;
      case 'unlinked_manual_cost':  return <StatusPill tone="neutral">Manual cost</StatusPill>;
      case 'unlinked_no_cost':      return <StatusPill tone="amber">Unlinked</StatusPill>;
      default:                      return null;
    }
  })();
  const lineCost = (Number(ri.unit_cost || 0) * Number(ri.quantity || 0));
  const showCheckbox = ri.link_status === 'pending_review';

  return (
    <div
      className="px-4 py-3"
      style={{ borderBottom: isLast ? 'none' : '1px solid var(--border)' }}
    >
      <div className="flex items-center gap-3 flex-wrap">
        {showCheckbox ? (
          <input
            type="checkbox"
            checked={isSelected}
            onChange={onToggleSelected}
            aria-label={`Select ${ri.ingredient_name}`}
            style={{ cursor: 'pointer', accentColor: 'var(--accent-primary)' }}
          />
        ) : (
          <div style={{ width: 13 }} aria-hidden />
        )}
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium truncate" style={{ color: 'var(--text-primary)' }}>{ri.ingredient_name}</div>
          <div className="text-xs flex items-center gap-2 flex-wrap mt-0.5" style={{ color: 'var(--text-muted)' }}>
            <span style={{ fontVariantNumeric: 'tabular-nums' }}>{`${Number(ri.quantity)} ${ri.unit}`}</span>
            <span style={{ fontVariantNumeric: 'tabular-nums' }}>{ri.link_status === 'unlinked_no_cost' ? '\u2014' : fmt$(lineCost)}</span>
            {linkedFoodItem && (ri.link_status === 'linked' || ri.link_status === 'pending_review') && (
              <span className="truncate" style={{ maxWidth: 240 }} title={linkedFoodItem.normalized_name}>
                {`\u2192 ${linkedFoodItem.normalized_name}`}
              </span>
            )}
            {linkedFoodItem?.needs_review && (
              <StatusPill tone="amber">Linked item has data review flag</StatusPill>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {stateChip}
          {/* Per-state actions */}
          {ri.link_status === 'pending_review' && (
            <>
              {ri.food_item_id && (
                <PrimaryBtn onClick={onConfirm} className="!text-xs !px-2.5 !py-1">Confirm</PrimaryBtn>
              )}
              <SecondaryBtn onClick={onOpenLinkSearch} className="!text-xs !px-2.5 !py-1">{ri.food_item_id ? 'Change' : 'Find match'}</SecondaryBtn>
              <SecondaryBtn onClick={onOpenManualCost} className="!text-xs !px-2.5 !py-1">Manual</SecondaryBtn>
              <SecondaryBtn onClick={onMarkUnlinked} className="!text-xs !px-2.5 !py-1">Leave unpriced</SecondaryBtn>
            </>
          )}
          {ri.link_status === 'unlinked_no_cost' && (
            <>
              <SecondaryBtn onClick={onOpenLinkSearch} className="!text-xs !px-2.5 !py-1">Find match</SecondaryBtn>
              <SecondaryBtn onClick={onOpenManualCost} className="!text-xs !px-2.5 !py-1">Manual cost</SecondaryBtn>
            </>
          )}
          {ri.link_status === 'unlinked_manual_cost' && (
            <>
              <SecondaryBtn onClick={onOpenManualCost} className="!text-xs !px-2.5 !py-1">Edit cost</SecondaryBtn>
              <SecondaryBtn onClick={onOpenLinkSearch} className="!text-xs !px-2.5 !py-1">Find match</SecondaryBtn>
            </>
          )}
          {ri.link_status === 'linked' && (
            <>
              <SecondaryBtn onClick={onOpenLinkSearch} className="!text-xs !px-2.5 !py-1">Change</SecondaryBtn>
              <SecondaryBtn onClick={onMarkUnlinked} className="!text-xs !px-2.5 !py-1">Mark unlinked</SecondaryBtn>
            </>
          )}
        </div>
      </div>

      {/* Inline link search */}
      {linkSearchOpen && (
        <InlineLinkSearch
          query={linkSearchQuery}
          onQueryChange={onLinkSearchQueryChange}
          foodItems={foodItems}
          ingredientName={ri.ingredient_name}
          onPick={onPickFoodItem}
          onClose={onCloseLinkSearch}
        />
      )}

      {/* Inline manual cost */}
      {manualCostOpen && (
        <div className="mt-2 rounded-lg p-3" style={{ background: 'var(--bg-subtle)', border: '1px solid var(--border)' }}>
          <div className="flex items-center gap-2 flex-wrap">
            <label className="text-xs" style={{ color: 'var(--text-secondary)' }}>{`Cost per ${ri.unit}`}</label>
            <div className="relative">
              <span className="absolute left-2 top-1/2 -translate-y-1/2 text-sm" style={{ color: 'var(--text-muted)' }}>$</span>
              <input
                type="number" step="0.01" autoFocus value={manualCostInput}
                onChange={e => onManualCostInputChange(e.target.value)}
                className="rounded-lg pl-6 pr-2 py-1.5 text-sm focus:outline-none"
                style={{ width: 110, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-primary)' }}
              />
            </div>
            <PrimaryBtn onClick={onSaveManualCost} className="!text-xs !px-3 !py-1.5">Save cost</PrimaryBtn>
            <SecondaryBtn onClick={onCloseManualCost} className="!text-xs !px-3 !py-1.5">Cancel</SecondaryBtn>
          </div>
          <div className="text-xs mt-2" style={{ color: 'var(--text-muted)', lineHeight: 1.5 }}>
            This won't auto-update when invoice prices change. You can re-link to a catalog item later.
          </div>
        </div>
      )}
    </div>
  );
}

function InlineLinkSearch({ query, onQueryChange, foodItems, ingredientName, onPick, onClose }) {
  const [browseAll, setBrowseAll] = useState(false);
  const matches = useMemo(() => {
    if (browseAll) {
      return foodItems
        .slice()
        .sort((a, b) => (a.normalized_name || '').localeCompare(b.normalized_name || ''))
        .slice(0, 200);
    }
    const initial = query || ingredientName || '';
    return fuzzyMatchFoodItems(initial, foodItems, 5);
  }, [browseAll, query, ingredientName, foodItems]);

  return (
    <div className="mt-2 rounded-lg p-3" style={{ background: 'var(--bg-subtle)', border: '1px solid var(--border)' }}>
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <input
          type="text" value={query} autoFocus
          onChange={e => onQueryChange(e.target.value)}
          placeholder={ingredientName ? `Searching: ${ingredientName}` : 'Search catalog'}
          className="flex-1 rounded-lg px-3 py-1.5 text-sm focus:outline-none"
          style={{ border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-primary)' }}
        />
        <SegmentedPills
          value={browseAll ? 'browse' : 'top5'}
          onChange={(v) => setBrowseAll(v === 'browse')}
          options={[
            { key: 'top5', label: 'Top matches' },
            { key: 'browse', label: 'Browse all' },
          ]}
          size="sm"
        />
        <SecondaryBtn onClick={onClose} className="!text-xs !px-2.5 !py-1">Cancel</SecondaryBtn>
      </div>
      <div className="rounded-lg overflow-y-auto" style={{ maxHeight: 240, background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
        {matches.length === 0 ? (
          <div className="px-3 py-4 text-center text-sm" style={{ color: 'var(--text-muted)' }}>
            {query ? 'No catalog items match.' : 'Start typing to search.'}
          </div>
        ) : (
          matches.map((fi, i) => (
            <div
              key={fi.id}
              onClick={() => onPick(fi, fi._matchScore)}
              className="px-3 py-2 cursor-pointer"
              style={{
                borderBottom: i < matches.length - 1 ? '1px solid var(--border)' : 'none',
                color: 'var(--text-primary)',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg-hover)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate" style={{ color: 'var(--text-primary)' }}>{fi.normalized_name}</div>
                  <div className="text-xs" style={{ color: 'var(--text-muted)' }}>
                    {fi.category || 'Uncategorized'}
                    {fi.is_catch_weight && ' \u00B7 catch-weight'}
                    {fi.current_price != null && ` \u00B7 ${fmt$Short(fi.current_price)}/${fi.unit || 'unit'}`}
                  </div>
                </div>
                {!browseAll && fi._matchScore != null && (
                  <span className="text-xs" style={{ color: 'var(--text-muted)', fontVariantNumeric: 'tabular-nums' }}>
                    {Math.round(fi._matchScore * 100)}%
                  </span>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function BulkConfirmModal({ count, running, onCancel, onConfirm }) {
  return (
    <div
      style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 60 }}
      onClick={onCancel}
    >
      <div
        className="rounded-lg p-6 w-full max-w-md mx-4"
        style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', boxShadow: '0 4px 16px rgba(0,0,0,0.12)' }}
        onClick={e => e.stopPropagation()}
      >
        <h3 className="text-base font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
          {`Confirm ${count} ingredient${count === 1 ? '' : 's'} as linked?`}
        </h3>
        <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)', lineHeight: 1.5 }}>
          {`This will mark ${count === 1 ? 'this ingredient' : 'all selected ingredients'} as linked to their current AI-suggested food items. The action will be logged in the audit trail but cannot be batch-undone — you'd have to fix each one individually.`}
        </p>
        <div className="flex items-center justify-end gap-2">
          <SecondaryBtn onClick={onCancel} disabled={running} className="!text-sm">Cancel</SecondaryBtn>
          <PrimaryBtn onClick={onConfirm} disabled={running} className="!text-sm">
            {running ? 'Confirming\u2026' : `Confirm ${count}`}
          </PrimaryBtn>
        </div>
      </div>
    </div>
  );
}
